import React from 'react';
import { useData } from '../lib/DataContext.jsx';
import { KpiCard, SectionTitle, ProvenanceFooter, BandPill, ProvenanceBadge } from '../components/ui/kits.jsx';
import { HBars, Lollipop, ScatterTrend } from '../components/charts/charts.jsx';
import { fmt, fmtInt, TOOL_COLOR, bandColor } from '../lib/format.js';

export default function InterventionBudget() {
  const { data, agg: A } = useData();
  if (!data || !A) return null;
  const { interventions, themeStrength, correlation } = data;
  const top = interventions[0];
  const strongestTheme = themeStrength[0];

  const points = A.scatter;
  const rTint = correlation.r >= 0.3 ? '#1f8a4c' : correlation.r <= -0.3 ? '#c8452f' : '#3c4762';

  return (
    <div style={{ padding: 22, display: 'flex', flexDirection: 'column', gap: 18 }} data-testid="page-budget">
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 14 }}>
        <KpiCard testid="kpi-interventions" hero label="Interventions Assessed" value={String(interventions.length)} sub="Across 4 thematic tools" />
        <KpiCard testid="kpi-top-intervention"
          label="Top Intervention"
          value={fmt(top.avg, 2)}
          sub={`${top.intervention} · ${top.tool}`}
          band={top.avg >= 4.6 ? 'Excellent' : top.avg >= 3.6 ? 'Good' : top.avg >= 2.6 ? 'Moderate' : 'Poor'}
        />
        <KpiCard testid="kpi-strongest-theme"
          label="Strongest Theme"
          value={strongestTheme.tool}
          sub={`Avg ${fmt(strongestTheme.avg, 2)} · ${strongestTheme.interventions} interventions`}
        />
        <div className="kpi-card fade-up" data-testid="kpi-correlation">
          <div className="kpi-label">Budget × Score</div>
          <div className="kpi-value tabular" style={{ color: rTint }}>
            {fmt(correlation.r, 3)}
          </div>
          <div className="kpi-sub">r · n={correlation.n} · <b>{correlation.strength}</b></div>
        </div>
      </div>

      <div className="card" style={{ padding: 18 }}>
        <SectionTitle right={
          <div style={{ display: 'flex', gap: 12, fontSize: 10.5, color: 'var(--ink-3)', fontWeight: 600, alignItems: 'center' }}>
            <ProvenanceBadge kind="live" />
            {['NRM', 'H&H', 'SDLE', 'PoE'].map((t) => (
              <div key={t} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <span style={{ width: 10, height: 10, background: TOOL_COLOR[t === 'H&H' ? 'Health' : (t === 'PoE' ? 'POE' : t)], borderRadius: 2 }} />
                {t}
              </div>
            ))}
          </div>
        }>
          Intervention Ranking · sorted by score
        </SectionTitle>
        <div className="scroll-y" style={{ maxHeight: 480 }}>
          <Lollipop
            testid="intervention-lollipop"
            rows={interventions.map((iv) => ({
              label: iv.intervention,
              value: iv.avg,
              color: TOOL_COLOR[iv.tool === 'H&H' ? 'Health' : (iv.tool === 'PoE' ? 'POE' : iv.tool)],
              sub: `${iv.tool} · n=${fmtInt(iv.n)}`,
            }))}
          />
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: 14 }}>
        <div className="card" style={{ padding: 18 }}>
          <SectionTitle right={
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <ProvenanceBadge kind="live" />
              <span style={{ fontSize: 11, color: 'var(--ink-4)' }}>{points.length} funded projects</span>
            </div>
          }>
            Budget vs Overall Score
          </SectionTitle>
          <ScatterTrend testid="scatter-trend" points={points} />
          <div style={{ marginTop: 8, display: 'flex', gap: 12, fontSize: 10.5, color: 'var(--ink-3)', fontWeight: 600, flexWrap: 'wrap' }}>
            {['Excellent', 'Good', 'Moderate', 'Poor'].map((b) => (
              <div key={b} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <span style={{ width: 10, height: 10, background: bandColor(b), borderRadius: 50 }} />{b}
              </div>
            ))}
            <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <svg width="20" height="4"><line x1="0" y1="2" x2="20" y2="2" stroke="var(--ink-3)" strokeDasharray="4 3" strokeWidth="1.5" /></svg>
              Linear trend
            </div>
          </div>
        </div>

        <div className="card" style={{ padding: 18 }}>
          <SectionTitle right={<ProvenanceBadge kind="live" />}>Theme Strength</SectionTitle>
          <Lollipop
            testid="theme-lollipop"
            rows={themeStrength.map((t) => ({
              label: t.tool,
              value: t.avg,
              color: TOOL_COLOR[t.tool === 'H&H' ? 'Health' : (t.tool === 'PoE' ? 'POE' : t.tool)],
              sub: `${t.interventions} interventions`,
            }))}
          />
        </div>
      </div>

      <ProvenanceFooter text={`Budget–score correlation is ${correlation.strength.toLowerCase()} (r=${fmt(correlation.r, 3)}, n=${correlation.n}). Investment level does not appear to systematically drive OECD outcomes at this sample size.`} />
    </div>
  );
}
